// src/components/admin/AdminSidebar.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../lib/AppContext';

const NAV_ITEMS = [
  { icon: '📊', label: 'Dashboard',  path: '/admin' },
  { icon: '📦', label: 'Products',   path: '/admin/products' },
  { icon: '🛒', label: 'Orders',     path: '/admin/orders' },
  { icon: '📝', label: 'Blog',       path: '/admin/blog' },
  { icon: '🖼️', label: 'Slides',    path: '/admin/slides' },
  { icon: '🎟️', label: 'Discounts', path: '/admin/discounts' },
  { icon: '🎰', label: 'Wheel',     path: '/admin/wheel' },
  { icon: '⚖️', label: 'Compliance', path: '/admin/compliance' },
  { icon: '✉️', label: 'Newsletter', path: '/admin/newsletter' },
  { icon: '👥', label: 'Customers',  path: '/admin/customers' },
  { icon: '📈', label: 'Analytics',  path: '/admin/analytics' },
];

function SidebarContent({ navigate, pathname, profile, onClose }) {
  const isActive = (path) => path === '/admin' ? pathname === '/admin' : pathname.startsWith(path);

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', paddingTop:16, paddingBottom:24 }}>
      {/* Admin badge */}
      <div style={{ padding:'0 16px 16px', borderBottom:'1px solid rgba(255,255,255,.06)', marginBottom:8 }}>
        <div style={{ background:'rgba(76,202,116,.1)', border:'1px solid rgba(76,202,116,.2)', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(76,202,116,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Bebas Neue', sans-serif", fontSize:'1rem', color:'#4cca74', flexShrink:0 }}>
            {(profile?.full_name || 'A').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:'.78rem', fontWeight:700, letterSpacing:'.04em', color:'#f4f4f4', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:120 }}>
              {profile?.full_name || 'Admin'}
            </div>
            <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:'.65rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'#4cca74' }}>
              ⚙ {profile?.role || 'admin'}
            </div>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav style={{ flex:1, padding:'4px 10px', overflowY:'auto' }}>
        {NAV_ITEMS.map(item => {
          const active = isActive(item.path);
          return (
            <button key={item.path} onClick={() => { navigate(item.path); onClose?.(); }} style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'10px 12px', marginBottom:2, background: active?'rgba(76,202,116,.1)':'transparent', border:`1px solid ${active?'rgba(76,202,116,.2)':'transparent'}`, borderRadius:10, cursor:'pointer', transition:'all .15s', textAlign:'left' }}
              onMouseOver={e => { if (!active) e.currentTarget.style.background='rgba(255,255,255,.04)'; }}
              onMouseOut={e  => { if (!active) e.currentTarget.style.background='transparent'; }}
            >
              <span style={{ fontSize:'1.1rem', flexShrink:0 }}>{item.icon}</span>
              <span style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:'.85rem', fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color: active?'#4cca74':'#777', transition:'color .15s' }}>
                {item.label}
              </span>
              {active && <div style={{ marginLeft:'auto', width:4, height:4, borderRadius:'50%', background:'#4cca74', flexShrink:0 }} />}
            </button>
          );
        })}
      </nav>

      {/* Back to store */}
      <div style={{ padding:'0 10px' }}>
        <button onClick={() => { navigate('/'); onClose?.(); }} style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'transparent', border:'1px solid rgba(255,255,255,.08)', borderRadius:10, cursor:'pointer' }}
          onMouseOver={e => e.currentTarget.style.borderColor='rgba(76,202,116,.3)'}
          onMouseOut={e  => e.currentTarget.style.borderColor='rgba(255,255,255,.08)'}
        >
          <span style={{ fontSize:'1rem' }}>←</span>
          <span style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:'.82rem', fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'#555' }}>Back to Store</span>
        </button>
      </div>
    </div>
  );
}

export default function AdminSidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { profile } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Desktop sidebar */}
      <div className="admin-sidebar-desktop" style={{ width:220, flexShrink:0, background:'#0d0d0d', borderRight:'1px solid rgba(255,255,255,.07)', minHeight:'calc(100vh - 88px)' }}>
        <SidebarContent navigate={navigate} pathname={pathname} profile={profile} />
      </div>

      {/* Mobile hamburger toggle */}
      <button
        className="admin-sidebar-toggle"
        onClick={() => setMobileOpen(o => !o)}
        aria-label="Toggle admin menu"
        style={{ display:'none' }}
      >
        {mobileOpen ? '✕' : '☰'}
      </button>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:498 }}
        />
      )}

      {/* Mobile drawer */}
      <div className="admin-sidebar-drawer" style={{
        position:'fixed', top:0, left: mobileOpen ? 0 : '-260px',
        width:240, height:'100%', zIndex:499,
        background:'#0d0d0d', borderRight:'1px solid rgba(255,255,255,.07)',
        transition:'left .25s ease', overflowY:'auto',
        display:'none', // shown via CSS at ≤768px
      }}>
        <SidebarContent navigate={navigate} pathname={pathname} profile={profile} onClose={() => setMobileOpen(false)} />
      </div>

      <style>{`
        @media (max-width: 768px) {
          .admin-sidebar-desktop { display: none !important; }
          .admin-sidebar-toggle {
            display: flex !important;
            position: fixed;
            bottom: 80px;
            right: 16px;
            z-index: 500;
            background: #4cca74;
            color: #000;
            border: none;
            border-radius: 50%;
            width: 48px;
            height: 48px;
            font-size: 1.2rem;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 20px rgba(0,0,0,.4);
            cursor: pointer;
          }
          .admin-sidebar-drawer { display: block !important; }
        }
      `}</style>
    </>
  );
}